import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Form, Button, InputGroup, Modal, Pagination } from 'react-bootstrap';
import { Search, Filter, Eye, Download, Users, CheckCircle2, Clock, XCircle } from 'lucide-react';
import '../../../../assets/css/admin/dashboardCSS/StudentManagement.css';
import Adminlayout from '../../../../layouts/admin/Adminlayout';
import TitleBar from '../../../../subscreens/admin/TitleBar';

const StudentManagement = () => {
  // State Management
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Students Data
  const [students] = useState([
    {
      id: 'ST-101',
      name: 'Ali Ahmed',
      email: 'ali@example.com',
      phone: '0300-1234567',
      institute: 'ABC University',
      department: 'Computer Science',
      semester: '5th',
      cnic: '12345-1234567-1',
      status: 'Pending',
      registeredDate: '2024-01-15',
      totalRequests: 2,
      address: 'House 123, Street 45, Lahore'
    },
    {
      id: 'ST-102',
      name: 'Sara Khan',
      email: 'sara@example.com',
      phone: '0301-9876543',
      institute: 'XYZ College',
      department: 'Business Administration',
      semester: '3rd',
      cnic: '54321-7654321-1',
      status: 'Verified',
      registeredDate: '2024-01-10',
      totalRequests: 1,
      address: 'House 456, Street 78, Karachi'
    },
    {
      id: 'ST-103',
      name: 'Ahmed Raza',
      email: 'ahmed@example.com',
      phone: '0302-5551234',
      institute: 'PQR Institute',
      department: 'Electrical Engineering',
      semester: '7th',
      cnic: '67890-9876543-1',
      status: 'Rejected',
      registeredDate: '2024-01-20',
      totalRequests: 0,
      address: 'House 789, Street 12, Islamabad'
    },
    {
      id: 'ST-104',
      name: 'Fatima Ali',
      email: 'fatima@example.com',
      phone: '0303-7778888',
      institute: 'ABC University',
      department: 'Medicine',
      semester: '2nd',
      cnic: '11111-2222222-1',
      status: 'Verified',
      registeredDate: '2024-01-05',
      totalRequests: 3,
      address: 'House 321, Street 99, Lahore'
    },
    {
      id: 'ST-105',
      name: 'Hassan Malik',
      email: 'hassan@example.com',
      phone: '0304-4445555',
      institute: 'LMN University',
      department: 'Mechanical Engineering',
      semester: '6th',
      cnic: '33333-4444444-1',
      status: 'Pending',
      registeredDate: '2024-01-25',
      totalRequests: 1,
      address: 'House 654, Street 33, Faisalabad'
    }
  ]);

  // Stats
  const stats = {
    total: students.length,
    verified: students.filter(s => s.status === 'Verified').length,
    pending: students.filter(s => s.status === 'Pending').length,
    rejected: students.filter(s => s.status === 'Rejected').length
  };

  // Filter Students
  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.institute.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'All' || student.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle View Student Details
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
  };

  // Export to CSV
  const handleExport = () => {
    console.log('Exporting student data...');
  };

  // Get Status Badge
  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'warning',
      'Verified': 'success',
      'Rejected': 'danger'
    };
    return variants[status] || 'secondary';
  };

  return (
    <Adminlayout>
     <div className="sm-student-management">
      <Container fluid>
         <TitleBar
                title="All Students"
                subtitle="Track student progress and engagement"
              ></TitleBar>

        {/* Stats Cards */}
        <Row className="sm-stats-row mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="sm-stat-card-sm">
              <Card.Body>
                <div className="sm-stat-content">
                  <div className="sm-stat-info">
                    <h4 className="sm-stat-value">{stats.total}</h4>
                    <p className="sm-stat-title">Total Students</p>
                  </div>
                  <div className="sm-stat-icon-wrapper total-icon">
                    <Users size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="sm-stat-card-sm">
              <Card.Body>
                <div className="sm-stat-content">
                  <div className="sm-stat-info">
                    <h4 className="sm-stat-value">{stats.verified}</h4>
                    <p className="sm-stat-title">Verified</p>
                  </div>
                  <div className="sm-stat-icon-wrapper verified-icon">
                    <CheckCircle2 size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="sm-stat-card-sm">
              <Card.Body>
                <div className="sm-stat-content">
                  <div className="sm-stat-info">
                    <h4 className="sm-stat-value">{stats.pending}</h4>
                    <p className="sm-stat-title">Pending</p>
                  </div>
                  <div className="sm-stat-icon-wrapper pending-icon">
                    <Clock size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="sm-stat-card-sm">
              <Card.Body>
                <div className="sm-stat-content">
                  <div className="sm-stat-info">
                    <h4 className="sm-stat-value">{stats.rejected}</h4>
                    <p className="sm-stat-title">Rejected</p>
                  </div>
                  <div className="sm-stat-icon-wrapper rejected-icon">
                    <XCircle size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Search and Filter Section */}
        <Card className="sm-filter-card mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={6} md={6} className="mb-3 mb-md-0">
                <InputGroup>
                  <InputGroup.Text className="sm-search-icon">
                    <Search size={18} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, ID, or institute..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="sm-search-input"
                  />
                </InputGroup>
              </Col>

              <Col lg={3} md={3} className="mb-3 mb-md-0">
                <InputGroup>
                  <InputGroup.Text className="sm-filter-icon">
                    <Filter size={18} />
                  </InputGroup.Text>
                  <Form.Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="sm-filter-select"
                  >
                    <option value="All">All Status</option>
                    <option value="Verified">Verified</option>
                    <option value="Pending">Pending</option>
                    <option value="Rejected">Rejected</option>
                  </Form.Select>
                </InputGroup>
              </Col>

              <Col lg={3} md={3}>
                <Button className="sm-export-btn" onClick={handleExport}>
                  <Download size={18} />
                  Export CSV
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Students Table */}
        <Card className="sm-table-card">
          <Card.Body>
            <div className="table-responsive">
              <Table hover className="sm-students-table">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Institute</th>
                    <th>Department</th>
                    <th>Semester</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentStudents.length > 0 ? (
                    currentStudents.map((student) => (
                      <tr key={student.id}>
                        <td className="sm-student-id">{student.id}</td>
                        <td className="sm-student-name">{student.name}</td>
                        <td>{student.institute}</td>
                        <td>{student.department}</td>
                        <td>{student.semester}</td>
                        <td>
                          <Badge bg={getStatusBadge(student.status)} className="sm-table-status-badge">
                            {student.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="sm-action-buttons">
                            <button
                              className="sm-action-btn sm-view-btn-action"
                              onClick={() => handleViewStudent(student)}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <p className="sm-no-data">No students found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredStudents.length > 0 && (
              <div className="sm-pagination-wrapper">
                <Pagination className="sm-pagination">
                  <Pagination.First 
                    onClick={() => paginate(1)} 
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => paginate(currentPage - 1)} 
                    disabled={currentPage === 1}
                  />
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === currentPage}
                      onClick={() => paginate(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}

                  <Pagination.Next 
                    onClick={() => paginate(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => paginate(totalPages)} 
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Student Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton className="sm-modal-header">
          <Modal.Title className="sm-modal-title">Student Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <div className="sm-student-details">
              <Row>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Student ID</p>
                  <p className="sm-detail-value">{selectedStudent.id}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Full Name</p>
                  <p className="sm-detail-value">{selectedStudent.name}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Email</p>
                  <p className="sm-detail-value">{selectedStudent.email}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Phone</p>
                  <p className="sm-detail-value">{selectedStudent.phone}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">CNIC</p>
                  <p className="sm-detail-value">{selectedStudent.cnic}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Institute</p>
                  <p className="sm-detail-value">{selectedStudent.institute}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Department</p>
                  <p className="sm-detail-value">{selectedStudent.department}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Semester</p>
                  <p className="sm-detail-value">{selectedStudent.semester}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Registered Date</p>
                  <p className="sm-detail-value">{selectedStudent.registeredDate}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p className="sm-detail-label">Total Requests</p>
                  <p className="sm-detail-value">{selectedStudent.totalRequests}</p>
                </Col>
                <Col md={12} className="mb-3">
                  <p className="sm-detail-label">Address</p>
                  <p className="sm-detail-value">{selectedStudent.address}</p>
                </Col>
                <Col md={12}>
                  <p className="sm-detail-label">Status</p>
                  <Badge bg={getStatusBadge(selectedStudent.status)} className="sm-status-badge-lg">
                    {selectedStudent.status}
                  </Badge>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </Adminlayout>
  );
};

export default StudentManagement;